import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../../../../config';

/**
 * Shared lookup loader for report filter bars. Replaces the 6 ad-hoc
 * useEffect POSTs duplicated in every report view.
 *
 * Base lookups (properties, groups, categories, subcategories, tenants,
 * floors) fire in a single Promise.allSettled on mount — one failing
 * endpoint (e.g. a 404 for an empty list) must not blank the others.
 * Results cached module-level (5-min TTL) keyed by endpoint+payload so
 * navigating between reports doesn't refetch the same reference data.
 *
 * Pass the current selection to get dependent-filtered derivations:
 *   const lk = useReportLookups({ propertyId, categoryId, subcategoryId, groupName });
 *   lk.propertyOptions / categoryOptions / subcategoryOptions /
 *   groupOptions / tenantOptions / floorOptions   // [{value,label,sub?}]
 *   lk.properties / categories / ... (raw arrays)
 *   lk.propertyAccessNames, lk.loading, lk.error
 */

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

function cacheGet(key) {
    const hit = cache.get(key);
    if (!hit) return null;
    if (Date.now() > hit.expiresAt) { cache.delete(key); return null; }
    return hit.data;
}
function cacheSet(key, data) { cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS }); }
export function invalidateReportLookups() { cache.clear(); }

async function postCached(endpoint, payload, headers) {
    const key = `POST:${endpoint}:${JSON.stringify(payload || {})}`;
    const hit = cacheGet(key);
    if (hit) return hit;
    const res = await axios.post(endpoint, payload, { headers });
    cacheSet(key, res.data);
    return res.data;
}

const arr = (x) => (Array.isArray(x) ? x : []);

const uniqueFloors = (floors) => {
    const seen = new Set();
    return arr(floors).filter((f) => {
        const name = f?.floor_id?.floor_name;
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
    });
};

const useReportLookups = ({ propertyId = '', categoryId = '', subcategoryId = '', groupName = '' } = {}) => {
    const concessionaireId = typeof window !== 'undefined' ? localStorage.getItem('concessionaireId') : '';
    const propertyAccess = typeof window !== 'undefined' ? localStorage.getItem('propertyAccess') : '';
    const token = typeof window !== 'undefined' ? localStorage.getItem('sessiontoken') : '';
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [properties, setProperties] = useState([]);
    const [groups, setGroups] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allSubcategories, setAllSubcategories] = useState([]);
    const [allTenants, setAllTenants] = useState([]);
    const [allFloors, setAllFloors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!concessionaireId) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError('');
        const H = config.hostname;
        Promise.allSettled([
            postCached(`${H}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaireId, property_id: propertyAccess }, headers),
            postCached(`${H}/tenant/getgroupnames`, { tenant_concessionaire_id: concessionaireId, property_id: propertyAccess }, headers),
            postCached(`${H}/category/getcategorybyconcessionaire`, { category_concessionaire_id: concessionaireId }, headers),
            postCached(`${H}/subcategory/getsubcategorybyconcessionaire`, { subcategory_concessionaire_id: concessionaireId }, headers),
            postCached(`${H}/tenant/getenantsbyconcessionaire`, { tenant_concessionaire_id: concessionaireId, property_id: propertyAccess }, headers),
            postCached(`${H}/property/getfloorsbyconcessionaire`, { concessionaire_id: concessionaireId, property_id: propertyAccess }, headers),
        ])
            .then((settled) => {
                if (cancelled) return;
                const val = (i) => (settled[i].status === 'fulfilled' ? settled[i].value : null);
                setProperties(arr(val(0)));
                setGroups(arr(val(1)));
                setCategories(arr(val(2)));
                setAllSubcategories(arr(val(3)));
                setAllTenants(arr(val(4)));
                setAllFloors(arr(val(5)));
                const failed = settled.filter((r) => r.status === 'rejected');
                failed.forEach((r, i) => console.warn(`useReportLookups: lookup failed`, r.reason?.response?.status || r.reason?.message));
                // Only surface an error if the core property/tenant lookups failed.
                if (settled[0].status === 'rejected' && settled[4].status === 'rejected') {
                    setError('Could not load filter options. Check your connection and retry.');
                }
            })
            .catch((err) => { if (!cancelled) { console.error('useReportLookups:', err); setError('Could not load filter options.'); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [concessionaireId, propertyAccess, headers]);

    const propertyAccessNames = useMemo(
        () => properties.map((p) => p.property_name).join(','),
        [properties],
    );

    // Dependent derivations
    const subcategories = useMemo(() => {
        if (!categoryId) return allSubcategories;
        return allSubcategories.filter((s) => s.category_id?._id === categoryId || s.category_id === categoryId);
    }, [allSubcategories, categoryId]);

    const tenants = useMemo(() => {
        let t = allTenants;
        if (groupName) t = t.filter((x) => x.tenant_group_name === groupName);
        if (categoryId) t = t.filter((x) => x.tenant_category_id?._id === categoryId);
        if (propertyId) t = t.filter((x) => x.tenant_property_id?._id === propertyId);
        if (subcategoryId) t = t.filter((x) => x.tenant_subcategory_id?._id === subcategoryId);
        return t;
    }, [allTenants, groupName, categoryId, propertyId, subcategoryId]);

    const floors = useMemo(() => {
        let f = allFloors;
        if (propertyId) f = f.filter((x) => x.property_id === propertyId);
        return uniqueFloors(f);
    }, [allFloors, propertyId]);

    // Pre-shaped option arrays for ReportFilterBar / MultiSelect
    const propertyOptions = useMemo(() => properties.map((p) => ({ value: p._id, label: p.property_name })), [properties]);
    const categoryOptions = useMemo(() => categories.map((c) => ({ value: c._id, label: c.category_name })), [categories]);
    const subcategoryOptions = useMemo(() => subcategories.map((s) => ({ value: s._id, label: s.subcategory_name })), [subcategories]);
    const groupOptions = useMemo(() => groups.map((g) => ({ value: g, label: g })), [groups]);
    const tenantOptions = useMemo(() => tenants.map((t) => {
        const prop = t.tenant_property_id?.property_name;
        const sub = [t.tenant_code, prop].filter(Boolean).join(' · ');
        return { value: t._id, label: t.tenant_store_name, sub };
    }), [tenants]);
    const floorOptions = useMemo(() => floors.map((f) => ({ value: f.floor_id.floor_name, label: f.floor_id.floor_name })), [floors]);

    return {
        concessionaireId,
        propertyAccess,
        propertyAccessNames,
        loading,
        error,
        // raw
        properties, groups, categories, subcategories, tenants, floors,
        // options
        propertyOptions, categoryOptions, subcategoryOptions, groupOptions, tenantOptions, floorOptions,
    };
};

export default useReportLookups;
